import React, { useState } from "react";
import { Modal, Box, Typography, IconButton, Paper, Divider, Grid, Tooltip } from "@mui/material";
import { IoClose } from "react-icons/io5";
import DataTable from "../DataTable/DataTable";
import DocumentViewer from "../DocumentHub/DocumentViewer";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "85%",
  maxWidth: "1000px",
  height: "85vh",
  maxHeight: "85vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: 2,
  overflowY: "auto",
  zIndex: 1300,
};

const MatchedSofDocsModal = ({ open, onClose, data, isLoading }) => {
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleOpenPdf = (url) => {
    if (!url) return;
    setPdfUrl(url);
    setPdfLoading(true);
    setPdfOpen(true);
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="matched-sof-docs-modal"
    >
      <Box sx={style}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: "bold" }}>
            Matched SOF Documents
          </Typography>
          <IconButton onClick={onClose} size="small">
            <IoClose size={24} />
          </IconButton>
        </div>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading...</Typography>
          </Box>
        ) : !data || !data.status ? (
          <Box sx={{ p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography>No matching SOF documents found.</Typography>
          </Box>
        ) : (
          <Box>
            {/* Invoice Reference Summary */}
            {data.invoice_reference && (
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#374151" }}>
                  Invoice Reference
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">Service ID</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.invoice_reference.service_id}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">Invoice ID</Typography>
                    {data.invoice_reference.invoice_pdf_url ? (
                      <Typography 
                        variant="body2" 
                        sx={{ fontWeight: 500, color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => handleOpenPdf(data.invoice_reference.invoice_pdf_url)}
                      >
                        {data.invoice_reference.invoice_id}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.invoice_reference.invoice_id}</Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">Invoice Doc ID</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.invoice_reference.invoice_doc_id}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">Classification</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{data.invoice_reference.discrepancy_classification ? data.invoice_reference.discrepancy_classification.replace(/_/g, ' ') : "N/A"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">Is Doc ID Matched In SOF Docs</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: data.invoice_reference.is_doc_id_matched_in_sof_docs ? "#2e7d32" : "inherit" }}>
                      {data.invoice_reference.is_doc_id_matched_in_sof_docs ? "Yes" : "No"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">Billed Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {Number(data.invoice_reference.billed_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">Contract Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {Number(data.invoice_reference.contract_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block">Discrepancy Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: data.invoice_reference.discrepancy_amount > 0 ? "error.main" : "success.main" }}>
                      {Number(data.invoice_reference.discrepancy_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
                {data.invoice_reference.discrepancy_classification_description && (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px dashed #e5e7eb" }}>
                    <Typography variant="caption" color="textSecondary" display="block">Description</Typography>
                    <Typography variant="body2">{data.invoice_reference.discrepancy_classification_description}</Typography>
                  </Box>
                )}
                {data.invoice_reference.installment_info && (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px dashed #e5e7eb" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151", mb: 1 }}>Installment Info</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary" display="block">Is Installment Billed</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {data.invoice_reference.installment_info.is_installment_billed ? "Yes" : "No"}
                        </Typography>
                      </Box>
                      {data.invoice_reference.installment_info.is_installment_billed && (
                        <>
                          <Box>
                            <Typography variant="caption" color="textSecondary" display="block">Plan Months</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {data.invoice_reference.installment_info.installment_plan_months || "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="textSecondary" display="block">Per Installment Amount</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {data.invoice_reference.installment_info.per_installment_amount !== undefined && data.invoice_reference.installment_info.per_installment_amount !== null
                                ? Number(data.invoice_reference.installment_info.per_installment_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                : "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="textSecondary" display="block">Within Plan</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {data.invoice_reference.installment_info.within_plan ? "Yes" : "No"}
                            </Typography>
                          </Box>
                          {data.invoice_reference.installment_info.note && (
                            <Box sx={{ width: '100%' }}>
                              <Typography variant="caption" color="textSecondary" display="block">Note</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {data.invoice_reference.installment_info.note}
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                )}
              </Paper>
            )}

            {/* SOF Docs */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              {data.msg || "SOF Documents"}
            </Typography>

            {data.sof_docs && data.sof_docs.length > 0 ? (
              data.sof_docs.map((docObj, index) => {
                const docId = Object.keys(docObj)[0];
                const docData = docObj[docId];
                
                return (
                  <Paper key={index} elevation={0} sx={{ mb: 3, overflow: "hidden", border: "1px solid #e5e7eb", borderRadius: 2 }}>
                    <Box sx={{ bgcolor: "#f3f4f6", borderBottom: "1px solid #e5e7eb", px: 2, py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151" }}>
                          SOF Document: {index + 1}
                        </Typography>
                        {docData.sof_info?.sof_pdf_url && (
                          <Typography 
                            variant="caption" 
                            sx={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline", fontWeight: 600 }}
                            onClick={() => handleOpenPdf(docData.sof_info.sof_pdf_url)}
                          >
                            (View PDF)
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", columnGap: 3, rowGap: 0.5, flexWrap: "wrap" }}>
                        <Typography variant="caption" color="textSecondary">Contract ID: <strong style={{ color: "#111827" }}>{docData.sof_info?.contract_id || "-"}</strong></Typography>
                        <Typography variant="caption" color="textSecondary">Doc ID: <strong style={{ color: "#111827" }}>{docData.sof_info?.doc_id || "-"}</strong></Typography>
                        <Typography variant="caption" color="textSecondary">Doc Date: <strong style={{ color: "#111827" }}>{docData.sof_info?.doc_date || "-"}</strong></Typography>
                        <Typography variant="caption" color="textSecondary">Ref ID: <strong style={{ color: "#111827" }}>{docData.sof_info?.ref_id || "-"}</strong></Typography>
                        <Typography variant="caption" color="textSecondary">Is Invoice Doc ID Match: <strong style={{ color: docData.sof_info?.is_invoice_doc_id_match ? "#2e7d32" : "#111827" }}>{docData.sof_info?.is_invoice_doc_id_match ? "Yes" : "No"}</strong></Typography>
                        <Typography variant="caption" color="textSecondary">Service Total MRC (USD): <strong style={{ color: "#111827" }}>{Number(docData.sof_info?.service_total_mrc_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></Typography>
                        <Typography variant="caption" color="textSecondary">Service Total NRC (USD): <strong style={{ color: "#111827" }}>{Number(docData.sof_info?.service_total_nrc_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></Typography>
                      </Box>
                    </Box>

                    {/* SOF Items */}
                    <Box sx={{ p: 1.5 }}>
                      
                      {/* SOF Items List */}
                      {docData.sof_items && docData.sof_items.length > 0 ? (
                        <DataTable
                          columns={[
                            { field: "service_id", headerName: "Service ID", width: 120 },
                            { 
                              field: "service_name", 
                              headerName: "Service Name", 
                              width: 250,
                              renderCell: (params) => {
                                const description = params.row.service_description;
                                return description ? (
                                  <Tooltip title={description} arrow placement="top">
                                    <span style={{ cursor: "help", borderBottom: "1px dashed #7C2EA0" }}>
                                      {params.value}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <span>{params.value}</span>
                                );
                              }
                            },
                            { field: "location_id", headerName: "Location ID", width: 150 },
                            { 
                              field: "mrc_usd", 
                              headerName: "MRC (USD)", 
                              width: 120,
                              renderCell: (params) => params.value ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"
                            },
                            { 
                              field: "nrc_usd", 
                              headerName: "NRC (USD)", 
                              width: 120,
                              renderCell: (params) => params.value ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"
                            },
                            { field: "activity", headerName: "Activity", width: 120 },
                            { field: "currency", headerName: "Currency", width: 100 },
                          ]}
                          rows={docData.sof_items.map((item, idx) => ({ ...item, id: idx + 1 }))}
                          height={Math.min(52 * docData.sof_items.length + 120, 350)}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">No items found for this SOF document.</Typography>
                      )}
                    </Box>
                  </Paper>
                );
              })
            ) : (
              <Typography variant="body2" color="textSecondary">No SOF documents available.</Typography>
            )}
          </Box>
        )}
        
        {/* Document Viewer Modal */}
        <DocumentViewer
          open={pdfOpen}
          url={pdfUrl}
          loading={pdfLoading}
          onClose={() => setPdfOpen(false)}
          onIframeLoad={() => setPdfLoading(false)}
        />
      </Box>
    </Modal>
  );
};

export default MatchedSofDocsModal;
